import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import FormInput from "../../../../Components/v2/Form/FormInput";
import FormSelect from "../../../../Components/v2/Form/FormSelect";
import Button from "../../../../Components/v2/Button";
import FormError from "../../../../Components/v2/Form/FormError";
import LoadErrorState from "../../../../Components/v2/LoadErrorState";
import {
  useAuthState,
  useAccountingState,
  useAccountingDispatch,
  requestListAccount,
  useBusinessProductState,
  useBusinessProductDispatch,
  useNotificationDispatch,
  addLocalNotification,
  requestListProductCategories,
  requestCreateProductCategory,
  requestUpdateProductCategory,
} from "../../../../Context";

const ProductCategories = () => {
  const { t } = useTranslation();

  const { user } = useAuthState();
  const businessID = user?.mainBusiness?.id;

  const productDispatch = useBusinessProductDispatch();
  const {
    productCategories,
    productCategoriesLoading,
    productCategoriesError,
    newCategoryLoading,
    newCategoryError,
    updateCategoryLoading,
    updateCategoryError,
  } = useBusinessProductState();

  const accountingDispatch = useAccountingDispatch();
  const { accounts, accountLoading, accountError } = useAccountingState();

  const notificationDispatch = useNotificationDispatch();

  const [name, setName] = useState("");
  const [account, setAccount] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const wasSaving = useRef(false);
  const wasCreating = useRef(false);

  // Keep what was typed until the create succeeds, so a failure can be retried.
  useEffect(() => {
    if (wasCreating.current && !newCategoryLoading && !newCategoryError) {
      setName("");
      setAccount(null);
    }
    wasCreating.current = newCategoryLoading;
  }, [newCategoryLoading, newCategoryError]);

  // When a row save finishes, surface success/error and clear the row spinner.
  useEffect(() => {
    if (wasSaving.current && !updateCategoryLoading) {
      addLocalNotification(notificationDispatch, {
        id: `update-category-${Date.now()}`,
        type: updateCategoryError ? "error" : "success",
        title: updateCategoryError ? "common.error" : "common.success",
        body: updateCategoryError
          ? "products.categories.updateError"
          : "products.categories.updateSuccess",
        showFull: true,
      });
      setSavingId(null);
    }
    wasSaving.current = updateCategoryLoading;
  }, [updateCategoryLoading, updateCategoryError, notificationDispatch]);

  const loadCategories = () => {
    if (!businessID) return;
    requestListProductCategories(productDispatch, {
      businessID,
      page: 1,
      limit: 100,
    });
  };

  const loadAccounts = () => {
    if (!businessID) return;
    requestListAccount(accountingDispatch, {
      businessID,
      page: 1,
      limit: 100,
      loadAll: true,
    });
  };

  // Load categories and the full chart of accounts (for the account picker).
  useEffect(() => {
    loadCategories();
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessID]);

  // Purchase non-goods lines debit an expense account, so only expense-side
  // categories are offered (GOODS lines auto-route to 110200 Persediaan).
  const accountOptions = useMemo(
    () =>
      (accounts || [])
        .filter((acc) =>
          ["COST_OF_GOODS_SOLD", "EXPENSE", "OTHER_EXPENSE"].includes(
            acc.category,
          ),
        )
        .map((acc) => ({
          ...acc,
          label: acc.code ? `${acc.code} - ${acc.name}` : acc.name,
          value: acc.id,
        })),
    [accounts],
  );

  // Built from the full account list so an existing mapping still renders even
  // if its account falls outside the expense-only picker options.
  const accountById = useMemo(() => {
    const map = {};
    (accounts || []).forEach((acc) => {
      map[acc.id] = {
        ...acc,
        label: acc.code ? `${acc.code} - ${acc.name}` : acc.name,
        value: acc.id,
      };
    });
    return map;
  }, [accounts]);

  const onCreate = () => {
    if (!name.trim() || !businessID) return;
    const input = {
      businessID,
      name: name.trim(),
      accountID: account?.value || null,
    };
    requestCreateProductCategory(productDispatch, { input });
  };

  const onChangeAccount = (category, selected) => {
    setSavingId(category.id);
    requestUpdateProductCategory(productDispatch, {
      input: {
        id: category.id,
        accountID: selected?.value || null,
      },
    });
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {t("products.categories.label", "Kategori Produk")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t(
              "products.categories.description",
              "Petakan setiap kategori ke akun beban. Baris pembelian non-barang akan didebit ke akun ini (default 660100 Biaya Umum & Administratif bila kosong).",
            )}
          </p>
        </div>

        {/* Create */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:items-end">
            <FormInput
              label="products.categories.name"
              id="categoryName"
              name="categoryName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("products.categories.namePlaceholder", "Nama kategori")}
            />
            <FormSelect
              label="products.categories.account"
              id="categoryAccount"
              name="categoryAccount"
              options={accountOptions}
              value={account}
              onChange={setAccount}
              isLoading={accountLoading}
              searchable
              isClearable
              onClear={() => setAccount(null)}
              placeholder={t("products.categories.accountPlaceholder", "Pilih akun")}
            />
            <div>
              <Button
                type="button"
                onClick={onCreate}
                loading={newCategoryLoading}
                disabled={!name.trim() || newCategoryLoading}
              >
                {t("products.categories.add", "Tambah Kategori")}
              </Button>
            </div>
          </div>
          <FormError className="mt-2" error={newCategoryError} />
        </div>

        {/* List — plain rows (no overflow wrapper) so the account
            dropdown can overflow each row instead of being clipped. */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {/* The account pickers below are empty without this list. */}
          <FormError
            className="mb-4"
            error={accountError}
            title="products.categories.accountsLoadError"
            onRetry={loadAccounts}
          />
          {productCategoriesLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {t("common.loading", "Memuat")}...
            </p>
          ) : productCategoriesError ? (
            <LoadErrorState
              error={productCategoriesError}
              onRetry={loadCategories}
            />
          ) : productCategories.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {t("products.categories.empty", "Belum ada kategori")}
            </p>
          ) : (
            <ul className="divide-y divide-gray-200 rounded-md ring-1 ring-gray-200">
              {productCategories.map((item) => (
                <li
                  key={item.id}
                  className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-2 sm:items-center"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {item.name}
                  </span>
                  <FormSelect
                    id={`account-${item.id}`}
                    name={`account-${item.id}`}
                    options={accountOptions}
                    value={item.accountID ? accountById[item.accountID] : null}
                    onChange={(selected) => onChangeAccount(item, selected)}
                    isLoading={
                      accountLoading ||
                      (updateCategoryLoading && savingId === item.id)
                    }
                    disabled={
                      accountLoading ||
                      (updateCategoryLoading && savingId === item.id)
                    }
                    searchable
                    isClearable
                    onClear={() => onChangeAccount(item, null)}
                    placeholder={t(
                      "products.categories.accountFallback",
                      "Default (660100 Biaya Umum)",
                    )}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </AccountingWrapper>
  );
};

export default ProductCategories;
